import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/ui-components/ui/button';
import { AuroraBackground } from '@/ui-components/ui/shadcn-io/aurora-background';
import { FileQuestion, LayoutTemplate, Zap, ArrowLeft } from 'lucide-react';
import { usePostsGetTemplateInsights } from '@/lib/api/requests/posts.requests';
import { MostUsedTemplate } from '@/lib/api/interfaces/posts.interface';
import { GenericItem, DataViewGridCard } from '@/util-components/data-view/generic-card';
import { Skeleton } from '@/ui-components/ui/skeleton';

/**
 * Template Not Found Page
 * Displayed when a requested template ID is invalid or not found (404).
 * Shows a friendly error message and recommends most used templates.
 */
export const TemplateNotFound: React.FC = () => {
  const navigate = useNavigate();

  // Fetch recommendations (most used templates)
  const { data: templatesData, isLoading } = usePostsGetTemplateInsights({ page: 0, limit: 5 });

  // Mapper for template items (reused logic from StartPage to ensure consistency)
  const mapTemplateToGenericItem = (t: MostUsedTemplate): GenericItem => ({
    id: t.templateId,
    title: t.name,
    previewContent: (
      <div className='w-full h-full flex items-center justify-center overflow-hidden rounded-xl bg-slate-100/50 p-2'>
        {t.thumbnailUrl ? (
          <img 
            src={t.thumbnailUrl} 
            alt={t.name} 
            className="max-w-full max-h-full object-contain drop-shadow-sm rounded-sm"
            loading="lazy"
          />
        ) : (
          <div className="flex flex-col items-center justify-center text-gray-300">
            <LayoutTemplate className="h-12 w-12 mb-2" />
          </div>
        )}
      </div>
    ),
    metadata: {
      badgeText: (
        <div className="flex items-center gap-1">
          <Zap className="w-3 h-3 fill-blue-500 text-blue-500" />
          <span className="font-medium text-blue-600">{t.count}x</span>
        </div>
      )
    }
  });

  // Helper to extract data from paginated response
  const extractData = (res: any) => res?.docs || res?.data || [];
  
  const templatesToDisplay = extractData(templatesData).map(mapTemplateToGenericItem);

  return (
    <div className="h-full w-full overflow-y-auto">
      <AuroraBackground className="h-auto min-h-screen">
        <div className="w-full max-w-5xl px-6 relative z-10 py-32 flex flex-col items-center min-h-screen justify-center">
            
            {/* Error Message Section */}
            <div className="mx-auto flex flex-col items-center text-center mb-12 animate-in fade-in zoom-in duration-500">
                <div className="mb-6 grid place-items-center rounded-2xl bg-white/70 p-4 ring-1 ring-black/5 backdrop-blur shadow-sm">
                    <FileQuestion className="h-9 w-9 text-blue-600" />
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-3 tracking-tight">Template nicht gefunden</h3>
                <p className="text-gray-500 max-w-md mx-auto leading-relaxed mb-8 text-base">
                    Das angeforderte Template existiert nicht mehr oder wurde verschoben. 
                    Vielleicht ist eines dieser beliebten Templates passend für dich?
                </p>
                
                 <Button 
                   onClick={() => navigate('/post-generator/start')} 
                   variant="outline" 
                   className="h-11 px-8 rounded-xl border-gray-200 hover:bg-white hover:text-blue-600 hover:border-blue-100 transition-all shadow-sm"
                 >
                   <ArrowLeft className="h-4 w-4 mr-2" />
                   Zurück zur Übersicht
                 </Button>
            </div>

            {/* Recommendations Section */}
            <div className="w-full max-w-4xl animate-in slide-in-from-bottom-8 duration-700 delay-150 fill-mode-backwards">
                <div className="flex items-center justify-center gap-2 mb-6">
                    <div className="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent w-24"></div>
                    <div className="flex items-center gap-2 px-2">
                        <Zap className="w-4 h-4 text-blue-500" />
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Beliebte Templates</span>
                    </div>
                    <div className="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent w-24"></div>
                </div>

                <div className="flex flex-wrap justify-center gap-3 px-4 sm:px-0">
                     {isLoading ? (
                        Array.from({ length: 5 }).map((_, i) => (
                            <div key={i} className="flex flex-col gap-2 w-[calc(50%-0.5rem)] sm:w-40 md:w-44">
                                <Skeleton className="w-full aspect-square rounded-lg bg-white/40" />
                                <div className="space-y-1.5">
                                    <Skeleton className="h-3 w-3/4 rounded-full bg-white/40" />
                                    <Skeleton className="h-2 w-1/2 rounded-full bg-white/40" />
                                </div>
                            </div>
                        ))
                    ) : (
                        templatesToDisplay.map((template: any) => (
                            <DataViewGridCard 
                                key={template.id} 
                                item={template} 
                                actions={{
                                    onItemClick: () => navigate(`/post-generator/create-post?templateId=${template.id}`)
                                }}
                                className="w-[calc(50%-0.5rem)] sm:w-40 md:w-44 bg-white/60 backdrop-blur-md border-white/50 hover:bg-white/90 hover:shadow-md hover:scale-[1.02] transition-all duration-300 text-xs" 
                            />
                        ))
                    )}
                </div>
            </div>
            
        </div>
      </AuroraBackground>
    </div>
  );
};
